/// <reference types="react" />

import * as DTCResources from "DistributedTaskControls/Resources/TFS.Resources.DistributedTaskControls";
import {
  IInputControlPropsBase, IInputControlStateBase, InputControlType
} from "DistributedTaskControls/SharedControls/InputControls/Common";
import {
  InputBase
} from "DistributedTaskControls/SharedControls/InputControls/Components/InputComponentBase";
import {
  RepositoryPickerComponent
} from "DistributedTaskControls/SharedControls/InputControls/Components/RepositoryPickerComponent";
import { ServiceEndpointHttpClient } from "TFS/ServiceEndpoint/ServiceEndpointRestClient";
import { ServiceEndpoint, ServiceEndpointRequest, DataSourceDetails, ResultTransformationDetails } from "TFS/ServiceEndpoint/Contracts";
import { getClient } from "VSS/Service";
import * as VssContext from "VSS/Context";
import * as React from "react";
import * as Diag from "VSS/Diag";
import * as Utils_String from "VSS/Utils/String";
import { EndpointAuthorizationSchemes, DataSourceNames } from "DistributedTaskControls/Common/Common";
import * as Q from "q";

export interface IGitHubRepositoryPickerProps extends IInputControlPropsBase<string> {
  connectionId: string;
  onRepositoryChanged: (selectedValue: string, selectedValueKey: string, skipFetchinDependency: boolean) => void;
  allowVariables: boolean;
  initialValues: string[];
}

export class GitHubRepositoryPickerComponent extends InputBase<string, IGitHubRepositoryPickerProps, IInputControlStateBase<string>> {
  public getType(): string {
    return InputControlType.INPUT_TYPE_GITHUB_REPOSITORY_PICKER;
  }

  protected getControl(): JSX.Element {
    Diag.logVerbose(
      "[GitHubRepositoryPickerComponent.getControl]: Method called."
    );
    return this._renderGitHubRepositoryPicker();
  }

  private _renderGitHubRepositoryPicker(): JSX.Element {
    return (
      <RepositoryPickerComponent
        value={this.props.value}
        connectionId={this.props.connectionId}
        disabled={this.props.disabled}
        required={this.props.required}
        allowVariables={this.props.allowVariables}
        initialValues={this.props.initialValues}
        onRepositoryChanged={this.props.onRepositoryChanged}
        getRepositoryByNameDataSource={DataSourceNames.Repository}
        getInitialListEndpointRequest={this._getInitialListEndpointRequest}
        getSearchEndpointRequest={this._getSearchEndpointRequest}
        errorMessage={DTCResources.EnterRepositoryOrSearch}
        instanceId={Utils_String.generateUID()}
        ariaLabelledBy={this.getInputFieldLabelElementId()}
      />
    );
  }

  private _getInitialListEndpointRequest = (): IPromise<ServiceEndpointRequest> => {
    let serviceEndpointHttpClient = getClient(ServiceEndpointHttpClient);
    const projectId = VssContext.getDefaultWebContext().project.id;

    let endpointRequestPromise = Q.defer<ServiceEndpointRequest>();

    if (!this._serviceConnection || !Utils_String.equals(this._serviceConnection.id, this.props.connectionId, true)) {
      let endpointPromise = serviceEndpointHttpClient.getServiceEndpoints(projectId, null, null, [this.props.connectionId], null, false, false);
      endpointPromise.then((result: ServiceEndpoint[]) => {
        if (result && result.length === 1) {
          this._serviceConnection = result[0];
          this._resolveInitialListServiceEndpointRequest(endpointRequestPromise);
        }
      }, (error) => {
        endpointRequestPromise.reject(error);
      });
    }
    else {
      this._resolveInitialListServiceEndpointRequest(endpointRequestPromise);
    }

    return endpointRequestPromise.promise;
  }

  private _getSearchEndpointRequest = (searchKey: string): ServiceEndpointRequest => {
    let dataSourceDetails: DataSourceDetails;
    let dataSource: string;
    let parameters: { [key: string]: string };
    let repoName: string;
    let org: string;
    let searchWithinOrg: boolean = false;
    if (searchKey.includes("/")) {
      const searchStrings = searchKey.split("/");
      repoName = searchStrings[1];
      org = searchStrings[0];
      if (repoName.trim().length > 0 && org.trim().length > 0) {
        searchWithinOrg = true;
      }
    }
    if (searchWithinOrg) {
      dataSource = DataSourceNames.SearchRepositoriesByNameWithinOrg;
      parameters = { searchText: repoName, orgName: org };

    } else {
      dataSource = DataSourceNames.SearchRepositoriesByNameForTasks;
      parameters = { searchText: searchKey };
    }

    dataSourceDetails = {
      dataSourceName: dataSource,
      dataSourceUrl: null,
      resourceUrl: null,
      requestContent: null,
      requestVerb: "GET",
      parameters: parameters,
      resultSelector: null,
      headers: null,
      initialContextTemplate: null
    };

    const resultTransformationDetails: ResultTransformationDetails = {
      resultTemplate: null,
      callbackContextTemplate: null,
      callbackRequiredTemplate: null
    };

    const serviceEndpointRequest: ServiceEndpointRequest = {
      serviceEndpointDetails: null,
      dataSourceDetails: dataSourceDetails,
      resultTransformationDetails: resultTransformationDetails
    };

    return serviceEndpointRequest;
  }

  private _resolveInitialListServiceEndpointRequest(dataSourcePromise: Q.Deferred<ServiceEndpointRequest>): void {
    let dataSource = DataSourceNames.UserRepositoriesAsOwner;
    if (this._serviceConnection && Utils_String.equals(this._serviceConnection.authorization.scheme, EndpointAuthorizationSchemes.InstallationToken, true)) {
      dataSource = DataSourceNames.InstallationRepos;
    }

    const dataSourceDetails: DataSourceDetails = {
      dataSourceName: dataSource,
      dataSourceUrl: null,
      resourceUrl: null,
      requestContent: null,
      requestVerb: "GET",
      parameters: null,
      resultSelector: null,
      headers: null,
      initialContextTemplate: null
    };

    const resultTransformationDetails: ResultTransformationDetails = {
      resultTemplate: null,
      callbackContextTemplate: null,
      callbackRequiredTemplate: null
    };

    const serviceEndpointRequest: ServiceEndpointRequest = {
      serviceEndpointDetails: null,
      dataSourceDetails: dataSourceDetails,
      resultTransformationDetails: resultTransformationDetails
    };

    dataSourcePromise.resolve(serviceEndpointRequest);
  }

  private _serviceConnection: ServiceEndpoint;
}
